/**
 * Weekdays picker — day chips + time zone (compact).
 */

import { useId, useMemo, type FC } from 'react';

import {
  formatIanaTimeZoneLabel,
  listVoluumOrderedTimeZones,
} from '@/lib/iana-time-zone-list';

import EnumListPicker from './EnumListPicker';

const WEEKDAY_LABEL: Record<string, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

export interface WeekdaysPickerProps {
  options: readonly string[];
  values: readonly string[];
  onChange: (next: readonly string[]) => void;
  timeZoneCode: string;
  onTimeZoneChange: (ianaId: string) => void;
  ariaLabel: string;
}

const WeekdaysPicker: FC<WeekdaysPickerProps> = ({
  options,
  values,
  onChange,
  timeZoneCode,
  onTimeZoneChange,
  ariaLabel,
}) => {
  const zones = useMemo(() => listVoluumOrderedTimeZones(), []);
  const safeTz = timeZoneCode.trim() || 'UTC';
  const tzSelectId = useId();

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <EnumListPicker
        options={options}
        values={values}
        onChange={onChange}
        ariaLabel={ariaLabel}
        formatOption={(opt) => WEEKDAY_LABEL[opt] ?? opt}
      />
      <label htmlFor={tzSelectId} className="sr-only">
        Time zone
      </label>
      <select
        id={tzSelectId}
        className="h-7 w-full max-w-md rounded-md border border-input bg-background px-1.5 text-[11px] text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        value={safeTz}
        onChange={(e) => onTimeZoneChange(e.target.value)}
        aria-label={`${ariaLabel} time zone`}
      >
        {!zones.includes(safeTz) ? (
          <option value={safeTz}>{formatIanaTimeZoneLabel(safeTz)}</option>
        ) : null}
        {zones.map((id) => (
          <option key={id} value={id}>
            {formatIanaTimeZoneLabel(id)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default WeekdaysPicker;
